import { Color, createTheme, Palette, Theme } from '@mui/material';

import { createClamp } from '../lib';

import { commonMixins } from './mixins';

import type { CSSProperties } from '@mui/material/styles';
import type {} from '@mui/x-charts/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

type CustomMixin = (
  palette: Palette,
  options?: {
    primaryOpacity?: number;
    secondaryOpacity?: number;
    prefix?: string;
  },
) => CSSProperties;
declare module '@mui/material/styles' {
  interface Mixins {
    severityStyles: CustomMixin;
    aircraftStatusStyles: CustomMixin;
    organStyles: CustomMixin;
    flightCategoryStyles: CustomMixin;
  }
}

declare module '@mui/material' {
  type VwRange =
    `${keyof Theme['breakpoints']['values']}-${keyof Theme['breakpoints']['values']}`;

  interface Theme {
    utils: {
      clamp: (min: number, max: number, vwRange?: VwRange) => string;
    };
  }

  interface ThemeOptions {
    utils?: Theme['utils'];
  }
}

declare module '@mui/material' {
  interface BreakpointOverrides {
    '2k': true;
    '4k': true;
  }

  interface Theme {
    columnSize: number;
    column: (multiplier: number) => number;
  }

  interface ThemeOptions {
    columnSize?: number;
  }

  interface TypographyVariants {
    red: React.CSSProperties;
    grey: React.CSSProperties;
    blue: React.CSSProperties;
    green: React.CSSProperties;
    violet: React.CSSProperties;
    yellow: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    red?: React.CSSProperties;
    grey?: React.CSSProperties;
    blue?: React.CSSProperties;
    green?: React.CSSProperties;
    violet?: React.CSSProperties;
    yellow?: React.CSSProperties;
  }

  interface Palette {
    blue: Color;
    mapBlue: Color;
    green: Color;
    violet: Color;
    orange: Color;
    mint: Color;
    yellow: Color;
    flightCategory: {
      IFR: string;
      MVFR: string;
      VFR: string;
      LIFR: string;
      NA: string;
    };
    organ: {
      liver: string;
      lungs: string;
      heart: string;
      /** The RGB midpoint of the liver colour and the map's new-case colour, by design decision. */
      kidney: string;
      /** An organ nothing could name. Neutral, so it never reads as a real organ. */
      unknown: string;
    };
    /**
     * OCS console artwork colours (TMDXNC-49).
     *
     * Two anchors per role, not a flat tint: each pairs a saturated fill
     * that carries the artwork's hue with a lighter, less saturated
     * highlight that the luminance mask blooms toward. Exact saturations
     * differ per role — pinned by `ocsConsolePalette.test.ts`, not restated
     * here.
     */
    ocsConsole: {
      liverFill: string;
      liverHighlight: string;
      heartFill: string;
      heartHighlight: string;
      lungsFill: string;
      lungsHighlight: string;
      technicalFill: string;
      technicalHighlight: string;
      certificationFill: string;
      /** Trial on a console that is on a case; see `theme-base.ts` for why it exists. */
      certificationFillSuppressed: string;
      certificationHighlight: string;
      certificationHighlightSuppressed: string;
    };
    aircraftStatus: {
      available: string;
      notAvailable: string;
      maintenance: string;
      aog: string;
    };
    severity: {
      critical: string;
      major: string;
      minor: string;
      low: string;
    };
    hubDashboard: {
      nowTime: string;
      nowLine: string;
      shift: {
        available: string;
        availableHighlight: string;
        noCall: string;
        travel: string;
        onCall: string;
        extraOnCall: string;
        atHub: string;
        phoneSupport: string;
        absence: string;
        unavailable: string;
      };
    };
  }

  interface PaletteOptions {
    blue: Color;
    mapBlue: Color;
    green: Color;
    violet: Color;
    orange: Color;
    mint: Color;
    yellow: Color;
  }
}
// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    red: true;
    grey: true;
    blue: true;
    green: true;
    violet: true;
    yellow: true;
  }
}

export const defaultTheme: Theme = createTheme({
  spacing: 8,
  columnSize: 160,
  breakpoints: {
    values: {
      xs: 480,
      sm: 768,
      md: 1024,
      lg: 1440,
      xl: 1920,
      '2k': 2560, // Add a custom breakpoint for 2K displays
      '4k': 3840, // Add a custom breakpoint for 4K displays
    },
  },
  // Add common mixins
  mixins: commonMixins,
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          // Map the new variant to render a <h1> by default
          red: 'strong',
          grey: 'strong',
          blue: 'strong',
          green: 'strong',
          violet: 'strong',
          yellow: 'strong',
        },
      },
    },
    MuiChartsSurface: {
      styleOverrides: {
        root: {
          touchAction: 'auto',
        },
      },
    },
    // We don't need to include common components in the default theme
    // as they will be included in each specific theme
  },
  typography: {
    // fontFamily: `'Avenir Next', Arial, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',
    // 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
    fontFamily: '"Inter", sans-serif',
    red: {
      color: '#FF3844',
    },
    grey: {
      color: '#D1D9E4',
    },
  },
});

defaultTheme.utils = {
  clamp: createClamp(defaultTheme),
};

// Add column helper to theme instance
defaultTheme.column = (multiplier: number): number =>
  defaultTheme.columnSize * multiplier;
